'use client';

import { useState, useEffect, useRef } from 'react';
import AsciiAnimation from './AsciiAnimation';
import { getBootAnimation, getTheme } from '@/lib/commands';
import type { AnimationName } from '@/lib/ascii-animations';
import type { ThemeName } from '@/lib/commands';

const ANIM_OPACITY: Record<ThemeName, Record<AnimationName, number>> = {
  default:  { interference: 0.07, fireworks: 0.12, starfield: 0.12, lorenz: 0.10, orbitdecay: 0.12, accretion: 0.12 },
  mono:     { interference: 0.10, fireworks: 0.15, starfield: 0.15, lorenz: 0.12, orbitdecay: 0.15, accretion: 0.15 },
  paper:    { interference: 0.12, fireworks: 0.18, starfield: 0.18, lorenz: 0.14, orbitdecay: 0.18, accretion: 0.18 },
  pacman:   { interference: 0.10, fireworks: 0.14, starfield: 0.14, lorenz: 0.12, orbitdecay: 0.14, accretion: 0.14 },
  sepia:    { interference: 0.08, fireworks: 0.12, starfield: 0.12, lorenz: 0.10, orbitdecay: 0.12, accretion: 0.12 },
  bauhaus:  { interference: 0.12, fireworks: 0.18, starfield: 0.18, lorenz: 0.15, orbitdecay: 0.18, accretion: 0.18 },
};

const DIAG_LABELS = [
  'kernel 6.1.0-moment loaded',
  'memory: 32768 MB available',
  'cpu: 8 cores @ 3.2 GHz',
  'network: link established',
  'runtime: node + next.js ready',
  'database: connected (PostgreSQL 16)',
  'auth: github oauth configured',
  'prng: deterministic (seeded)',
  'security: sandbox configured',
  'services: online',
];

const TITLE = 'MOMENT';

const PIXEL_FONT: Record<string, number[][]> = {
  M: [
    [1,0,0,0,1],
    [1,1,0,1,1],
    [1,0,1,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
  ],
  O: [
    [0,1,1,1,0],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [0,1,1,1,0],
  ],
  E: [
    [1,1,1,1],
    [1,0,0,0],
    [1,1,1,0],
    [1,0,0,0],
    [1,1,1,1],
  ],
  N: [
    [1,0,0,0,1],
    [1,1,0,0,1],
    [1,0,1,0,1],
    [1,0,0,1,1],
    [1,0,0,0,1],
  ],
  T: [
    [1,1,1,1,1],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,0,1,0,0],
  ],
};

function computeRevealMap(): Map<string, number> {
  const map = new Map<string, number>();
  let idx = 0;
  for (let li = 0; li < TITLE.length; li++) {
    const font = PIXEL_FONT[TITLE[li]];
    for (let c = 0; c < 5; c++) {
      for (let r = 0; r < 5; r++) {
        if (font[r][c]) {
          map.set(`${li},${r},${c}`, idx++);
        }
      }
    }
  }
  return map;
}

const REVEAL_MAP = computeRevealMap();
const TOTAL_BITS = REVEAL_MAP.size;

export default function BootSequence({ onComplete }: { onComplete: () => void }) {
  const [phase, setPhase] = useState(0);
  const [diagLines, setDiagLines] = useState<string[]>([]);
  const [revealedBits, setRevealedBits] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    timers.push(setTimeout(() => setPhase(1), 200));
    timers.push(setTimeout(() => setPhase(2), 1000));
    timers.push(setTimeout(() => setPhase(3), 2200));
    timers.push(setTimeout(() => setPhase(4), 4000));
    timers.push(setTimeout(() => setPhase(5), 4800));

    const bitInterval = 1200 / TOTAL_BITS;
    for (let i = 0; i <= TOTAL_BITS; i++) {
      timers.push(setTimeout(() => setRevealedBits(i), 1000 + i * bitInterval));
    }

    for (let i = 0; i < DIAG_LABELS.length; i++) {
      timers.push(
        setTimeout(() => setDiagLines((prev) => [...prev, DIAG_LABELS[i]]), 2200 + i * 80)
      );
    }

    return () => timers.forEach(clearTimeout);
  }, []);

  const doneRef = useRef(false);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      if (doneRef.current) return;
      doneRef.current = true;
      onCompleteRef.current();
    };
    window.addEventListener('keydown', handler, true);
    window.addEventListener('click', handler, true);
    return () => {
      window.removeEventListener('keydown', handler, true);
      window.removeEventListener('click', handler, true);
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-surface flex flex-col z-50 cursor-pointer select-none">
      {phase >= 1 && (
        <div
          className="absolute left-0 right-0 h-[2px] bg-amber opacity-60"
          style={{
            animation: 'scan-down 0.8s linear forwards',
            boxShadow:
              '0 0 12px color-mix(in srgb, var(--color-amber) 60%, transparent), 0 0 40px color-mix(in srgb, var(--color-amber) 20%, transparent)',
          }}
        />
      )}

      <div
        className="absolute inset-0 transition-opacity duration-[2000ms] ease-in text-amber"
        style={{
          opacity: phase >= 2 ? ANIM_OPACITY[getTheme()]?.[getBootAnimation()] ?? 0.10 : 0,
        }}
      >
        <AsciiAnimation animation={getBootAnimation()} className="w-full h-full" />
      </div>

      <div className="relative z-10 w-full max-w-lg p-8 text-[11px] leading-relaxed text-dim">
        {phase >= 2 && (
          <div className="flex gap-[6px] mb-4">
            {TITLE.split('').map((ch, li) => (
              <div key={li} className="flex flex-col gap-[2px]">
                {PIXEL_FONT[ch].map((row, r) => (
                  <div key={r} className="flex gap-[2px]">
                    {row.map((bit, c) => {
                      const revIdx = REVEAL_MAP.get(`${li},${r},${c}`);
                      const isOn = bit === 1 && revIdx !== undefined && revIdx < revealedBits;
                      return (
                        <div
                          key={c}
                          className={`w-[5px] h-[5px] ${
                            isOn ? 'bg-amber' : 'bg-transparent'
                          }`}
                          style={isOn ? { boxShadow: '0 0 4px color-mix(in srgb, var(--color-amber) 50%, transparent)' } : undefined}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {phase >= 3 && diagLines.map((line, i) => (
          <div key={i} className="opacity-60">
            {line}
          </div>
        ))}
        {phase >= 4 && (
          <div className="mt-3 text-green text-xs glow-green font-medium">
            ALL SYSTEMS NOMINAL
          </div>
        )}
      </div>

      {phase >= 1 && (
        <div
          className="relative z-10 px-8 pb-8 mt-auto text-primary text-[10px] tracking-widest uppercase"
          style={{ animation: 'fade-in 0.5s ease forwards' }}
        >
          press any key to {phase >= 5 ? 'continue' : 'skip'}
        </div>
      )}
    </div>
  );
}
